import React, { useCallback } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { ru } from 'date-fns/locale/ru';
import 'react-datepicker/dist/react-datepicker.css';
import { MODES, parseValue, formatValue, displayFormat } from './valueFormat';
import TimePanel from './TimePanel';
import './DateTimeInput.css';

registerLocale('ru', ru);

const PLACEHOLDERS = {
  [MODES.TIME]: 'чч:мм',
  [MODES.DATE]: 'дд.мм.гггг',
  [MODES.DATETIME]: 'дд.мм.гггг чч:мм',
};

/**
 * Общее ядро полей выбора даты и времени.
 *
 * Наружу компонент работает только строками (см. valueFormat.js): Date живёт
 * внутри. onChange вызывается либо с канонической строкой, либо с пустой —
 * недописанный ввод наружу не уходит, иначе такие строки утекают в запросы API.
 *
 * Время выбирается собственной панелью TimePanel (часы и минуты отдельными
 * списками); встроенный список react-datepicker скрыт стилями, потому что он
 * одноколоночный и ходит фиксированным шагом. Сам react-datepicker остаётся
 * движком попапа: позиционирование, закрытие по клику вне и разбор набранного
 * с клавиатуры текста берутся у него.
 */
const PickerBase = ({
  mode,
  value = '',
  onChange,
  min = '',
  max = '',
  disabled = false,
  placeholder,
  className = '',
  id,
  ariaLabel,
  onEnter,
}) => {
  const selected = parseValue(mode, value);

  const handleChange = useCallback(
    (date, event) => {
      if (date) {
        // Пока ввод не дописан (например, год ещё из одной-трёх цифр),
        // react-datepicker всё равно собирает из него Date — год в такой
        // дате получается короче четырёх цифр. formatValue тогда отдаёт
        // строку с обрезанным годом, а parseValue её не примет обратно
        // (её регэксп требует ровно 4 цифры года): используем это как
        // проверку «дата дописана целиком» и молча игнорируем всё остальное.
        const formatted = formatValue(mode, date);
        if (parseValue(mode, formatted) === null) return;
        onChange(formatted);
        return;
      }
      // react-datepicker отдаёт null и на очистке поля, и на недописанном вводе.
      // Пустым считаем только первое.
      const raw = event?.target?.value;
      if (raw === '' || raw === undefined) onChange('');
    },
    [mode, onChange],
  );

  const handleTimeSelect = useCallback(
    (hours, minutes) => {
      // Режим «только время» опирается на дату-якорь из valueFormat,
      // «дата + время» без выбранной даты — на сегодняшний день.
      const base = selected
        ? new Date(selected)
        : parseValue(mode, mode === MODES.TIME ? '00:00' : formatValue(MODES.DATETIME, new Date()));
      base.setHours(hours, minutes, 0, 0);
      onChange(formatValue(mode, base));
    },
    [mode, selected, onChange],
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter' && onEnter) onEnter();
    },
    [onEnter],
  );

  const timeOnly = mode === MODES.TIME;

  return (
    <DatePicker
      selected={selected}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      locale="ru"
      dateFormat={displayFormat(mode)}
      showTimeSelect={mode !== MODES.DATE}
      showTimeSelectOnly={timeOnly}
      // Встроенный список скрыт стилями; час вместо четверти часа оставляет
      // в скрытой разметке 24 пункта вместо 96.
      timeIntervals={60}
      minDate={timeOnly ? undefined : parseValue(mode, min) || undefined}
      maxDate={timeOnly ? undefined : parseValue(mode, max) || undefined}
      disabled={disabled}
      placeholderText={placeholder || PLACEHOLDERS[mode]}
      popperPlacement="bottom-start"
      calendarClassName="dti-calendar"
      customInput={
        <input
          id={id}
          aria-label={ariaLabel}
          className={`dti-input ${className}`.trim()}
          autoComplete="off"
        />
      }
    >
      {mode !== MODES.DATE && (
        <TimePanel
          hours={selected ? selected.getHours() : null}
          minutes={selected ? selected.getMinutes() : null}
          onSelect={handleTimeSelect}
        />
      )}
    </DatePicker>
  );
};

export default PickerBase;
